import { Box, CircularProgress, FormControl } from "@mui/material";
import Button from "@mui/material/Button";
import FormLabel from "@mui/material/FormLabel";
import React, { Fragment, SyntheticEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import SelectWithSearch from "src/components/SelectWithSearch/view";
import { formatGroupContactCountSuffix } from "src/components/GroupOptionLabel";
import { SaveAndStartJoinCampaignButton } from "src/modules/campaigns/components/SaveAndStartJoinCampaignButton";
import { getCurrentCompanyId } from "src/modules/companies/slices";
import { useGroupsApi } from "src/modules/groups/api";
import { Group } from "src/modules/groups/types";
import { ImportContactsModalStep } from "./ImportContactsModalStep";

type AddContactsToGroupsModalType = {
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  createdContactsIds: string[];
  existedContactsIds: string[];
  setImportContactsStep: React.Dispatch<React.SetStateAction<ImportContactsModalStep>>;
  handleClose: () => void;
  setModalTitle: (text: string) => void;
  onBack?: () => void;
};

export default function AddContactsToGroups({
  createdContactsIds,
  existedContactsIds,
  setModalTitle,
  error,
  setError,
  handleClose,
  onBack,
}: AddContactsToGroupsModalType) {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[] | null>(null);
  const [groups, setGroups] = useState<Group[]>();
  const { getGroups, addContactsToGroup, removeContactsFromGroup } = useGroupsApi();
  const [isContactsUploading, setIsContactsUploading] = useState(false);

  useEffect(() => {
    selectedCompanyId &&
      getGroups({
        companyId: selectedCompanyId,
        limit: 1000,
        offset: 0,
      })
        .then(({ data }) => {
          setGroups(data.items.filter((g) => g.type !== "default"));
        })
        .catch((e) => {
          console.log(e);
        });
  }, [selectedCompanyId]);

  useEffect(() => {
    setModalTitle(
      `Add ${createdContactsIds.length + (existedContactsIds?.length || 0)} imported contact${
        createdContactsIds.length > 1 ? "s" : ""
      } to group(s)`
    );
  }, []);

  const handleSelectedGroupsChange = (
    _: SyntheticEvent<Element, Event> | undefined,
    items: { text: string; value: string }[]
  ) => {
    const ids = items?.map(({ value }) => value) || [];
    setSelectedGroupIds(ids);
  };

  /** Resolves to whether all the contacts were added to the selected groups. */
  const assignContactsToGroups = async (contacts: string[]) => {
    if (!contacts || contacts.length === 0) {
      return true;
    }

    const promises = [];
    for (const groupId of selectedGroupIds!) {
      promises.push(addContactsToGroup(selectedCompanyId as string, groupId, contacts));
    }

    try {
      setIsContactsUploading(true);
      await Promise.all(promises);
      return true;
    } catch (e) {
      console.log(e);
      if (e.error === "validation") {
        setError(true);
      }
      return false;
    } finally {
      setIsContactsUploading(false);
    }
  };

  /** Resolves to whether all the contacts were moved to the selected groups. */
  const changeContactsGroups = async (contacts: string[]) => {
    const promises = [];

    if (!contacts || contacts.length === 0) {
      return true;
    }

    for (const groupId of selectedGroupIds!) {
      promises.push(removeContactsFromGroup(selectedCompanyId as string, groupId, contacts));
    }

    try {
      await Promise.all(promises);
      return await assignContactsToGroups(contacts);
    } catch (e) {
      console.log(e);
      if (e.error === "validation") {
        setError(true);
      }
      return false;
    }
  };

  /** Resolves to the imported contacts once they are all added to the groups. */
  const addToGroups = async () => {
    const existedContacts = existedContactsIds ?? [];
    const createdAdded = await assignContactsToGroups(createdContactsIds);
    const existedAdded = await changeContactsGroups(existedContacts);
    if (!createdAdded || !existedAdded) {
      return undefined;
    }
    handleClose();
    return [...createdContactsIds, ...existedContacts];
  };


  return (
    <Fragment>
      <FormControl sx={{ width: "100%", mb: 2.5 }}>
        <SelectWithSearch
          label="Add to group(s) (optional)"
          placeholder="Add to group(s) (optional)"
          options={
            groups
              ?.filter((g) => g.type === "custom")
              .map(({ name, id, validContactCount, joinCampaigns }) => ({
                text: name || '',
                secondaryText: formatGroupContactCountSuffix(validContactCount, joinCampaigns?.length),
                value: id,
              })) || []
          }
          values={selectedGroupIds || []}
          handleSelectChange={(e, items) => handleSelectedGroupsChange(e, items)}
          style={{ width: "100%", maxHeight: "100px" }}
        />
      </FormControl>

      {error && (
        <FormLabel error={true} style={{ marginTop: "10px", textAlign: "center" }}>
          {"Unable to add contacts to group contacts."}
        </FormLabel>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
        <Box>
          {onBack && (
            <Button
              variant="outlined"
              onClick={onBack}
              sx={{ minWidth: "auto" }}
            >
              Back
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            Skip
          </Button>
          <SaveAndStartJoinCampaignButton
            label="Add To Group(s)"
            mainButtonWidth="170px"
            disabled={!selectedGroupIds?.length || isContactsUploading}
            groupIds={selectedGroupIds ?? []}
            includeDefaultGroup
            onSave={addToGroups}
          >
            {isContactsUploading ? <CircularProgress size={25} /> : "Add To Group(s)"}
          </SaveAndStartJoinCampaignButton>
        </Box>
      </Box>
    </Fragment>
  );
}
